import type { Moment } from 'moment';
import { ReactNode, Ref, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { BRAND_NAME, USE_THETRARIS_LOGO } from '../../../../shared/env';
import { ISO_LOCAL_DATE } from '../../../../shared/utils/dateTime';
import mobileLogoIcon from '../../assets/icons/icon_mobile-logo.png';
import { BlueRoundedButton } from '../../common/components/BlueRoundedButton';
import { ColoredIcon } from '../../common/components/Icon';
import { LanguageSelector } from '../../common/components/LanguageSelector';
import { DateFieldSlot } from '../../common/components/Slot';
import { SlotRef } from '../../common/components/Slot/types';
import { Spacer } from '../../common/components/Spacer';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';
import { Agreements } from '../../keycloak-theme/login/features/SignUp/Agreements';

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  box-sizing: border-box;
  padding-top: 50px;
  padding-right: 20px;
  padding-left: 20px;
`;

const ErrorMessage = styled.div`
  max-width: 100%;
  height: 18px;
  margin-top: 8px;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #ff5656;
`;

const LogoHeading = styled.div`
  display: inline-flex;
  justify-content: center;
  font-weight: 1000;
  font-size: ${p => (p.theme.isMobile ? '19px' : '25px')};
  align-items: center;
  color: #2e5be0;
  flex-wrap: wrap;
  column-gap: 5px;
`;

const TitleStyle = styled.div`
  font-size: ${p => (p.theme.isMobile ? '19px' : '25px')};
  text-align: center;
`;

const Heading = styled.div`
  font-weight: 700;
  font-size: 18px;
  padding: 5px 0;
`;

const GenderButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
`;

const InsideWrapper = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  text-align: center;
`;

const AgreementWrapper = styled.div`
  display: flex;
  text-align: left;
  padding-left: ${p => (p.theme.isMobile ? '50px' : '20px')};
`;

const GenderButton = styled.button<{
  $isActive?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  height: 38px;
  width: 100%;
  max-width: 50px;
  margin-left: 10px;
  border-radius: 12px;
  white-space: nowrap;
  cursor: pointer;
  background-color: ${p => (p.$isActive ? '#2e5be0' : '#ffffff')};
  color: ${p => (p.$isActive ? '#ffffff' : '#929292')};
  border-color: ${p => (p.$isActive ? '#2e5be0' : '#e3e3e3')};
  border-style: solid;
  border-width: 2px;
`;

const TextWrapper = styled.div`
  margin-top: 30px;
`;

const ErrorMessageWrapper = styled.div`
  margin-bottom: 20px;
`;

type PreviewVerificationProps = PreviewVerificationFormProps &
  PreviewVerificationFailedMessageProps;
export const PreviewVerification = ({
  failure,
  ...props
}: PreviewVerificationProps) => {
  return failure < 5 ? (
    <PreviewVerificationForm failure={failure} {...props} />
  ) : (
    <PreviewVerificationFailedMessage {...props} />
  );
};

interface PreviewVerificationFormProps {
  failure: number;
  birthInput?: Ref<SlotRef>;
  onAnswer(birth: string, gender: string): void;
}
const PreviewVerificationForm = ({
  failure,
  birthInput,
  onAnswer,
}: PreviewVerificationFormProps) => {
  const isMobile = useContext(WindowSizeContext) === WindowSize.MOBILE;
  const { t: globalT } = useTranslation('translation');
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.shareToPatient',
  });

  const [agree, setAgree] = useState(false);
  const [gender, setGender] = useState('');
  const [birth, setBirth] = useState<Moment | null>(null);

  const handleSelectGender = (value: 'M' | 'F' | 'O') => {
    setGender(value);
  };

  const handleBirthChange = (value: Moment | null): void => {
    setBirth(value);
  };

  const isInvalid = !birth || gender === '' || !agree;

  const handleClickConfirmButton = () => {
    if (!birth) {
      return;
    }
    const birthString = birth.format(ISO_LOCAL_DATE);
    onAnswer(birthString, gender);
  };

  return (
    <FieldWrapper>
      <LogoHeading>
        {USE_THETRARIS_LOGO ? (
          BRAND_NAME
        ) : (
          <ColoredIcon
            width={isMobile ? '100px' : '120px'}
            height={isMobile ? '31px' : '31px'}
            imageWidth={isMobile ? '100px' : '120px'}
            imageUrl={mobileLogoIcon}
            color={'#2e5be0'}
          />
        )}
        <TitleStyle>{t('logoTitle')}</TitleStyle>
      </LogoHeading>
      <LanguageSelector icon={false} color="black" />
      <InsideWrapper>
        <Heading>{t('title')}</Heading>
        {failure !== 0 ? (
          <ErrorMessageWrapper>
            <ErrorMessage>
              {t('warningCount', {
                failure: failure,
              })}
            </ErrorMessage>
            <ErrorMessage>{t('warningTxt')}</ErrorMessage>
          </ErrorMessageWrapper>
        ) : (
          <Spacer height="72px" />
        )}
        {isMobile ? (
          <>
            <DateFieldSlot
              label={`${t('dob')} :`}
              ref={birthInput}
              date={birth}
              onChangeDate={handleBirthChange}
            />
            <GenderButtonWrapper>
              <GenderButton
                $isActive={gender === 'M'}
                onClick={() => handleSelectGender('M')}>
                {globalT('common.shortGender.male')}
              </GenderButton>
              <GenderButton
                $isActive={gender === 'F'}
                onClick={() => handleSelectGender('F')}>
                {globalT('common.shortGender.female')}
              </GenderButton>
              <GenderButton
                $isActive={gender === 'O'}
                onClick={() => handleSelectGender('O')}>
                {globalT('common.shortGender.others')}
              </GenderButton>
            </GenderButtonWrapper>
          </>
        ) : (
          <DateFieldSlot
            label={`${t('dob')} :`}
            ref={birthInput}
            date={birth}
            onChangeDate={handleBirthChange}>
            <GenderButton
              $isActive={gender === 'M'}
              onClick={() => handleSelectGender('M')}>
              {globalT('common.shortGender.male')}
            </GenderButton>
            <GenderButton
              $isActive={gender === 'F'}
              onClick={() => handleSelectGender('F')}>
              {globalT('common.shortGender.female')}
            </GenderButton>
            <GenderButton
              $isActive={gender === 'O'}
              onClick={() => handleSelectGender('O')}>
              {globalT('common.shortGender.others')}
            </GenderButton>
          </DateFieldSlot>
        )}
        <AgreementWrapper>
          <Agreements onChangeSatisfied={setAgree} />
        </AgreementWrapper>
        <BlueRoundedButton
          disabled={isInvalid}
          maxWidth="110px"
          marginTop="30px"
          onClick={handleClickConfirmButton}>
          {t('confirmBtn')}
        </BlueRoundedButton>
      </InsideWrapper>
    </FieldWrapper>
  );
};

interface PreviewVerificationFailedMessageProps {
  failedTitle?: string;
  failedMessage?: ReactNode;
}
const PreviewVerificationFailedMessage = ({
  failedTitle,
  failedMessage,
}: PreviewVerificationFailedMessageProps) => {
  const isMobile = useContext(WindowSizeContext) === WindowSize.MOBILE;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.shareToPatient',
  });

  const defaultFailedTitle = t('failTitle');
  const defaultFailedMessage = useMemo(
    () => [
      <div key={1}>{t('failTxt')}</div>,
      <div key={2}>{t('failTxt2')}</div>,
    ],
    [t],
  );

  return (
    <FieldWrapper>
      <LogoHeading>
        {USE_THETRARIS_LOGO ? (
          BRAND_NAME
        ) : (
          <ColoredIcon
            width={isMobile ? '100px' : '120px'}
            height={isMobile ? '31px' : '31px'}
            imageWidth={isMobile ? '100px' : '120px'}
            imageUrl={mobileLogoIcon}
            color={'#2e5be0'}
          />
        )}
        <TitleStyle>{t('logoTitle')}</TitleStyle>
      </LogoHeading>
      <InsideWrapper>
        <Heading>{failedTitle ?? defaultFailedTitle}</Heading>
        <TextWrapper>{failedMessage ?? defaultFailedMessage}</TextWrapper>
      </InsideWrapper>
    </FieldWrapper>
  );
};
